import React, { ReactNode } from "react";
import { LanguageContext } from "../common/language";
import IntroDialog from "./IntroDialog";
import { AlertBox } from "./AlertBox";
import { FieldValue, getAuth, getFirestore } from "@atgof-firebase/firebase";

type ErrorBoundaryProps = {
  toplevel?: boolean;
  children: ReactNode | undefined;
};
type ErrorBoundaryState = { error?: any; errorInfo?: any };

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static contextType = LanguageContext;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {};
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error(error, errorInfo);
    try {
      const errorString = error
        ? error instanceof Error
          ? error.toString()
          : typeof error === "string"
            ? error
            : undefined
        : undefined;
      let infoString: string | undefined;
      try {
        infoString = JSON.stringify(errorInfo);
      } catch (err) {
        console.error("Unable to serialize error info", err);
      }
      let uid: string | undefined;
      try {
        uid = getAuth().currentUser?.uid;
      } catch (err) {
        console.error("Unable to determine UID", err);
      }
      getFirestore()
        .collection("errors")
        .doc()
        .set({
          ...(errorString ? { error: errorString } : {}),
          ...(infoString ? { info: infoString } : {}),
          ...(uid ? { uid } : {}),
          timestamp: FieldValue.serverTimestamp(),
        });
    } catch (err) {
      console.error("Unable to report error", err);
    }
    this.setState({ ...this.state, error: error, errorInfo: errorInfo });
  }

  render() {
    const { toplevel } = this.props;
    const { error } = this.state;
    const { ph } = this.context as any;
    if (error) {
      const alertBox = (
        <AlertBox
          status="error"
          title={ph("unexpected-error") as string}
          message={
            ph(
              (toplevel ? "toplevel-" : "") + "unexpected-error-prompt",
            ) as string
          }
        />
      );
      return toplevel ? <IntroDialog>{alertBox}</IntroDialog> : alertBox;
    }
    return this.props.children || null;
  }
}
